import { Component, type ReactNode } from 'react';
import { reportError } from '@/lib/telemetry';
import { ReportControl } from '@/components/ui/ReportControl';
import { markStale } from '@/lib/updateAvailable';

/** Isolates a crashing surface; the crash-guard E2E suite fails the build when this text renders. */
export const ERROR_BOUNDARY_TEXT = 'Something went wrong on this page.';

/** A code chunk that can't be fetched (offline, or a deploy replaced it) — not a crash in the page itself. */
const CHUNK_ERROR = /dynamically imported module|Importing a module script failed|Loading chunk|ChunkLoadError|Unable to preload CSS/i;

/** `resetKey`: when it changes (the route boundary passes the path), a caught error is cleared — navigating away recovers. */
export class ErrorBoundary extends Component<{ children: ReactNode; resetKey?: string }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidUpdate(prev: { resetKey?: string }) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }
  componentDidCatch(error: Error) {
    console.error('[rmp2] surface crashed', error);
    reportError(error, 'boundary');
    // a newer build replaced this page's code: offer the update (markStale does nothing while offline)
    if (CHUNK_ERROR.test(String(error.message))) markStale();
  }
  render() {
    if (!this.state.error) return this.props.children;
    if (CHUNK_ERROR.test(String(this.state.error.message))) {
      const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
      return (
        <div role="alert" className="mx-auto my-16 max-w-md rounded-panel bg-surface p-8 text-center elevate" data-chunk-error>
          <h1 className="text-lg font-semibold text-ink">{offline ? "You're offline — this page can't open yet" : 'This page needs a refresh'}</h1>
          <p className="mt-2 text-sm text-ink-2">
            {offline
              ? "It hasn't been opened on this device since you lost the connection. Pages you already opened (like the check-in desk) keep working."
              : "This page couldn't open. Reload to get the latest version — your saved work is safe."}
          </p>
          <button className="mt-5 font-semibold text-ink underline decoration-brand decoration-2 underline-offset-4" onClick={() => window.location.reload()}>
            {offline ? 'Try again' : 'Reload the page'}
          </button>
        </div>
      );
    }
    return (
      <div role="alert" className="mx-auto my-16 max-w-md rounded-panel bg-surface p-8 text-center elevate" data-error-boundary>
        <h1 className="text-lg font-semibold text-ink">{ERROR_BOUNDARY_TEXT}</h1>
        <p className="mt-2 text-sm text-ink-2">Your saved work is safe. Reload the page to continue.</p>
        <button className="mt-5 font-semibold text-ink underline decoration-brand decoration-2 underline-offset-4" onClick={() => window.location.reload()}>
          Reload the page
        </button>
        {/* the explanation behind the info icon opens under this row, from its left edge */}
        <div className="relative mt-3 flex justify-center text-sm">
          <ReportControl surface="boundary" className="static"
            failure={{ reason: 'crash', message: ERROR_BOUNDARY_TEXT, details: `${this.state.error.name}: ${this.state.error.message}` }} />
        </div>
      </div>
    );
  }
}
